<script setup lang="ts">
  import type { StashColorNames, StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import { useCssModule } from 'vue';

  import colorSchemeUtil from '../../utils/colorScheme';
  import Icon from '../Icon/Icon.vue';

  defineOptions({
    name: 'll-chip',
  });

  export interface ChipProps {
    /**
     * The color for the chip that determines both the text and bg color. Needs to
     * be one of the brand colors in our design system (not a shade).
     */
    colorScheme?: StashColorNames;

    /**
     * The shade of the provided color to use for the background color. This effects the inferred
     * text color unless a specific text color is provided. Currently only `light` and `main`
     * shades are supported. `dark` may be added in the future.
     */
    shade?: 'light' | 'main';

    /**
     * The size of the chip.
     */
    size?: 'small' | 'medium';

    /**
     * The type of border radius to use.
     */
    radius?: 'none' | 'standard' | 'pill';

    /**
     * The background color of the chip. Needs to be one of our design system colors.
     */
    bgColor?: StashColorNamesWithShades;

    /**
     * The color of the chip text. Needs to be one of our design system colors.
     */
    textColor?: StashColorNamesWithShades | 'white';

    /**
     * Determines if a close icon is surfaced.
     */
    isRemovable?: boolean;

    /**
     * Determines if background and text color in Chip should be overidden.
     */
    shouldOverrideColors?: boolean;

    /**
     * Determines if the chip is disabled.
     */
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<ChipProps>(), {
    size: 'medium',
    radius: 'standard',
    colorScheme: 'ice',
    shade: 'light',
    bgColor: undefined,
    textColor: undefined,
    isRemovable: false,
    shouldOverrideColors: false,
    disabled: false,
  });

  const emit = defineEmits<{
    /**
     * Fires on click of the chip.
     */
    (e: 'click'): void;
    /**
     * If `is-removable` is true, fires on click of the close button.
     */
    (e: 'remove'): void;
  }>();

  const classes = useCssModule();

  function determineConditionalStyles() {
    const { computedBgColor, computedTextColor } = colorSchemeUtil({
      shade: props.shade,
      color: props.colorScheme,
    });

    const colorStyles = `text-${props.textColor || computedTextColor} bg-${props.bgColor || computedBgColor}`;

    return {
      'rounded-none': props.radius === 'none',
      'rounded-xs': props.radius === 'standard',
      'rounded-full': props.radius === 'pill',
      [colorStyles]: !props.shouldOverrideColors && !props.disabled,
      'bg-ice-500 text-white': props.disabled,
    };
  }
</script>

<template>
  <span
    class="stash-chip relative inline-flex items-center"
    :class="[
      `stash-chip--radius-${props.radius}`,
      `stash-chip--size-${props.size}`,
      `stash-chip--shade-${props.shade}`,
      classes.root,
      classes.removableChip,
      classes[`size-${props.size}`],
      determineConditionalStyles(),
    ]"
    data-test="stash-chip"
    @click="emit('click')"
  >
    <span v-if="isRemovable" :class="[{ 'mr-6 truncate': props.isRemovable }]">
      <!-- @slot Chip text -->
      <slot></slot>
    </span>

    <!-- Allows easier testing and DOM quering when the chip text isn't nested in an inner span -->
    <!-- @slot Chip text -->
    <slot v-else></slot>

    <button
      v-if="props.isRemovable"
      tabindex="-1"
      class="stash-chip__remove-button"
      :class="['absolute', classes['remove-button']]"
      data-test="stash-chip|remove-button"
      :disabled="props.disabled"
      @keypress.enter.prevent="emit('remove')"
      @mousedown.prevent.stop="emit('remove')"
    >
      <Icon data-test="icon|close" name="close" size="small" />
    </button>
  </span>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    /**
   * Applying these styles this way because the rule of thumb is that tailwind classes should
   * always end up taking precedence, so we're converted these specific styles to be generated
   * in a custom class instead, giving `FilterChip` (which wraps `Chip`) the ability to easily
   * override them.
   */
    @apply leading-none font-semibold uppercase text-[10px];
  }

  .size-medium {
    height: 20px;
    padding: 0 8px;
  }

  .size-small {
    height: 16px;
    padding: 0 8px;
  }

  .remove-button {
    border-radius: inherit;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    height: inherit;
    padding: 0 4px;
    right: 0;
  }

  .remove-button:not(:disabled):hover {
    background: rgb(0 0 0 / 10%);
  }
</style>
